import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { getErrorMessage } from '../utils/errorUtils';
import { formatRut, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Users,
  Plus,
  Search,
  Eye,
  Loader2,
  Phone,
  MapPin,
  Upload,
  Download,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Power,
  PowerOff
} from 'lucide-react';
import {
  CHILEAN_COMMUNES,
  HOUSING_TYPES,
  HOUSING_OWNERSHIP,
  EDUCATION_LEVELS,
  EMPLOYMENT_STATUS,
  RSH_TRAMOS,
  MARITAL_STATUS,
  BASIC_SERVICES,
} from '../lib/utils';

const Beneficiaries = () => {
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [editingBeneficiary, setEditingBeneficiary] = useState(null);
  const fileInputRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Por favor selecciona un archivo Excel válido (.xlsx o .xls)');
      e.target.value = '';
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/beneficiaries/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(
        `Importación completada: ${response.data.success} nuevos, ${response.data.skipped} omitidos.`
      );
      fetchBeneficiaries();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al importar archivo'));
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get('/beneficiaries/export', {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'] || '';
      const match = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
      const filename = match?.[1] || `beneficiarios_${new Date().toISOString().slice(0, 10)}.xlsx`;

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Exportación completada');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al exportar beneficiarios'));
    } finally {
      setExporting(false);
    }
  };

  // Form state
  const [formData, setFormData] = useState({
    rut: '',
    name: '',
    birth_date: '',
    age: 0,
    nationality: 'Chilena',
    marital_status: '',
    rsh_folio: '',
    rsh_tramo: '',
    address: '',
    comuna: '',
    phone: '',
    email: '',
    housing_type: '',
    housing_ownership: '',
    basic_services: [],
    education_level: '',
    employment_status: '',
    personal_income: 0,
    household_income: 0,
    vulnerability_reason: '',
    requested_aid_type: '',
    current_benefits: [],
    status: 'active',
    family_members: [],
  });

  const fetchBeneficiaries = useCallback(async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await api.get('/beneficiaries', { params });
      setBeneficiaries(response.data);
    } catch (error) {
      toast.error('Error al cargar beneficiarios');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    fetchBeneficiaries();
    setCurrentPage(1);
  }, [fetchBeneficiaries]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      basic_services: prev.basic_services.includes(service)
        ? prev.basic_services.filter(s => s !== service)
        : [...prev.basic_services, service]
    }));
  };

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleBirthDateChange = (e) => {
    const birthDate = e.target.value;
    const age = calculateAge(birthDate);
    setFormData(prev => ({ ...prev, birth_date: birthDate, age }));
  };

  const handleEditClick = (beneficiary) => {
    setEditingBeneficiary(beneficiary);
    setFormData({
      rut: beneficiary.rut || '',
      name: beneficiary.name || '',
      birth_date: beneficiary.birth_date || '',
      age: beneficiary.age || 0,
      nationality: beneficiary.nationality || 'Chilena',
      marital_status: beneficiary.marital_status || '',
      rsh_folio: beneficiary.rsh_folio || '',
      rsh_tramo: beneficiary.rsh_tramo || '',
      address: beneficiary.address || '',
      comuna: beneficiary.comuna || '',
      phone: beneficiary.phone || '',
      email: beneficiary.email || '',
      housing_type: beneficiary.housing_type || '',
      housing_ownership: beneficiary.housing_ownership || '',
      basic_services: beneficiary.basic_services || [],
      education_level: beneficiary.education_level || '',
      employment_status: beneficiary.employment_status || '',
      personal_income: beneficiary.personal_income || 0,
      household_income: beneficiary.household_income || 0,
      vulnerability_reason: beneficiary.vulnerability_reason || '',
      requested_aid_type: beneficiary.requested_aid_type || '',
      current_benefits: beneficiary.current_benefits || [],
      status: beneficiary.status || 'active',
      family_members: beneficiary.family_members || [],
    });
    setEditDialogOpen(true);
  };

  const handleToggleStatus = async (beneficiaryId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await api.put(`/beneficiaries/${beneficiaryId}`, {
        ...beneficiaries.find(b => b.beneficiary_id === beneficiaryId),
        status: newStatus
      });
      toast.success(`Estado cambiado a ${newStatus === 'active' ? 'Activo' : 'Inactivo'}`);
      fetchBeneficiaries();
    } catch (error) {
      toast.error('Error al cambiar el estado del beneficiario');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!formData.rut || !formData.name || !formData.birth_date || !formData.address || !formData.comuna || !formData.phone) {
      toast.error('Por favor complete todos los campos obligatorios');
      return;
    }

    setSubmitting(true);
    try {
      await api.put(`/beneficiaries/${editingBeneficiary.beneficiary_id}`, formData);
      toast.success('Beneficiario actualizado exitosamente');
      setEditDialogOpen(false);
      resetForm();
      fetchBeneficiaries();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al actualizar beneficiario'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.rut || !formData.name || !formData.birth_date || !formData.address || !formData.comuna || !formData.phone) {
      toast.error('Por favor complete todos los campos obligatorios');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/beneficiaries', formData);
      toast.success('Beneficiario registrado exitosamente');
      setDialogOpen(false);
      resetForm();
      fetchBeneficiaries();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al registrar beneficiario'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      rut: '',
      name: '',
      birth_date: '',
      age: 0,
      nationality: 'Chilena',
      marital_status: '',
      rsh_folio: '',
      rsh_tramo: '',
      address: '',
      comuna: '',
      phone: '',
      email: '',
      housing_type: '',
      housing_ownership: '',
      basic_services: [],
      education_level: '',
      employment_status: '',
      personal_income: 0,
      household_income: 0,
      vulnerability_reason: '',
      requested_aid_type: '',
      current_benefits: [],
      status: 'active',
      family_members: [],
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Activo</Badge>;
      case 'inactive':
        return <Badge variant="secondary">Inactivo</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pendiente</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      setSortConfig({ key: null, direction: 'asc' });
      return;
    }
    setSortConfig({ key, direction });
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronsUpDown className="w-3 h-3 ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-3 h-3 ml-1 text-teal-600" /> 
      : <ChevronDown className="w-3 h-3 ml-1 text-teal-600" />;
  };

  const sortedBeneficiaries = [...beneficiaries].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let aValue = a[sortConfig.key] || '';
    let bValue = b[sortConfig.key] || '';
    
    aValue = String(aValue).toLowerCase();
    bValue = String(bValue).toLowerCase();

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedBeneficiaries.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedBeneficiaries = sortedBeneficiaries.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="space-y-6" data-testid="beneficiaries-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
            Beneficiarios
          </h1>
          <p className="text-slate-500 mt-1">
            Gestión de personas que reciben asistencia social
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-center gap-2">
          {/* File input invisible */}
          <input 
            type="file" 
            ref={fileInputRef} 
            className="hidden" 
            accept=".xlsx, .xls"
            onChange={handleFileChange}
          />
          
          <Button 
            variant="outline" 
            className="w-full sm:w-auto bg-white text-teal-700 border-teal-200 hover:bg-teal-50"
            onClick={handleImportClick}
            disabled={importing}
          >
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Importar Excel
          </Button>

          <Button
            variant="outline"
            className="w-full sm:w-auto bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Exportar Excel
          </Button>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            if (open) {
              resetForm();
              setEditingBeneficiary(null);
            }
            setDialogOpen(open);
          }}>
            <DialogTrigger asChild>
              <Button className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 shadow-lg shadow-teal-600/20" data-testid="add-beneficiary-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Beneficiario
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">Registrar Nuevo Beneficiario</DialogTitle>
              </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Datos Personales */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 font-['Manrope']">
                  Datos de Identificación
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rut">RUT *</Label>
                    <Input
                      id="rut"
                      name="rut"
                      value={formData.rut}
                      onChange={handleInputChange}
                      placeholder="12.345.678-9"
                      className="bg-slate-50"
                      data-testid="rut-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre Completo *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Juan Pérez González"
                      className="bg-slate-50"
                      data-testid="name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="birth_date">Fecha de Nacimiento *</Label>
                    <Input
                      id="birth_date"
                      name="birth_date"
                      type="date"
                      value={formData.birth_date}
                      onChange={handleBirthDateChange}
                      className="bg-slate-50"
                      data-testid="birth-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nationality">Nacionalidad</Label>
                    <Input
                      id="nationality"
                      name="nationality"
                      value={formData.nationality}
                      onChange={handleInputChange}
                      className="bg-slate-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Estado Civil</Label>
                    <Select value={formData.marital_status} onValueChange={(v) => handleSelectChange('marital_status', v)}>
                      <SelectTrigger className="bg-slate-50">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {MARITAL_STATUS.map(status => (
                          <SelectItem key={status} value={status}>{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* RSH */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 font-['Manrope']">
                  Registro Social de Hogares
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rsh_folio">Folio RSH</Label>
                    <Input
                      id="rsh_folio"
                      name="rsh_folio"
                      value={formData.rsh_folio}
                      onChange={handleInputChange}
                      placeholder="123456789"
                      className="bg-slate-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tramo RSH</Label>
                    <Select value={formData.rsh_tramo} onValueChange={(v) => handleSelectChange('rsh_tramo', v)}>
                      <SelectTrigger className="bg-slate-50">
                        <SelectValue placeholder="Seleccionar tramo" />
                      </SelectTrigger>
                      <SelectContent>
                        {RSH_TRAMOS.map(tramo => (
                          <SelectItem key={tramo} value={tramo}>{tramo}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Domicilio */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 font-['Manrope']">
                  Datos de Domicilio
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="address">Dirección *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Calle, número, depto/casa, villa/población"
                      className="bg-slate-50"
                      data-testid="address-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Comuna *</Label>
                    <Select value={formData.comuna} onValueChange={(v) => handleSelectChange('comuna', v)}>
                      <SelectTrigger className="bg-slate-50" data-testid="comuna-select">
                        <SelectValue placeholder="Seleccionar comuna" />
                      </SelectTrigger>
                      <SelectContent>
                        {CHILEAN_COMMUNES.map(comuna => (
                          <SelectItem key={comuna} value={comuna}>{comuna}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Teléfono *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+56 9 1234 5678"
                      className="bg-slate-50"
                      data-testid="phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Correo Electrónico</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="correo@ejemplo.cl"
                      className="bg-slate-50"
                    />
                  </div>
                </div>
              </div>

              {/* Vivienda */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 font-['Manrope']">
                  Información de Vivienda
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Vivienda</Label>
                    <Select value={formData.housing_type} onValueChange={(v) => handleSelectChange('housing_type', v)}>
                      <SelectTrigger className="bg-slate-50">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {HOUSING_TYPES.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tenencia</Label>
                    <Select value={formData.housing_ownership} onValueChange={(v) => handleSelectChange('housing_ownership', v)}>
                      <SelectTrigger className="bg-slate-50">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {HOUSING_OWNERSHIP.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Servicios Básicos</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {BASIC_SERVICES.map(service => (
                        <Button
                          key={service}
                          type="button"
                          variant={formData.basic_services.includes(service) ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleServiceToggle(service)}
                          className={formData.basic_services.includes(service) ? 'bg-teal-600 hover:bg-teal-700' : ''}
                        >
                          {service}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Socioeconómico */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 font-['Manrope']">
                  Datos Socioeconómicos
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nivel Educacional</Label>
                    <Select value={formData.education_level} onValueChange={(v) => handleSelectChange('education_level', v)}>
                      <SelectTrigger className="bg-slate-50">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {EDUCATION_LEVELS.map(level => (
                          <SelectItem key={level} value={level}>{level}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Situación Laboral</Label>
                    <Select value={formData.employment_status} onValueChange={(v) => handleSelectChange('employment_status', v)}>
                      <SelectTrigger className="bg-slate-50">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {EMPLOYMENT_STATUS.map(status => (
                          <SelectItem key={status} value={status}>{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="personal_income">Ingreso Personal (CLP)</Label>
                    <Input
                      id="personal_income"
                      name="personal_income"
                      type="number"
                      value={formData.personal_income}
                      onChange={handleInputChange}
                      className="bg-slate-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="household_income">Ingreso Hogar (CLP)</Label>
                    <Input
                      id="household_income"
                      name="household_income"
                      type="number"
                      value={formData.household_income}
                      onChange={handleInputChange}
                      className="bg-slate-50"
                    />
                  </div>
                </div>
              </div>

              {/* Motivo */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 font-['Manrope']">
                  Motivo de la Consulta
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="vulnerability_reason">Descripción de la situación</Label>
                    <Textarea
                      id="vulnerability_reason"
                      name="vulnerability_reason"
                      value={formData.vulnerability_reason}
                      onChange={handleInputChange}
                      placeholder="Describa la situación de vulnerabilidad o necesidad..."
                      className="bg-slate-50 min-h-[100px]"
                    />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="bg-teal-600 hover:bg-teal-700"
                  disabled={submitting}
                  data-testid="submit-beneficiary-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    'Guardar Beneficiario'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Editar Beneficiario</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-6 mt-4">
              {/* Datos Personales */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 font-['Manrope']">
                  Datos de Identificación
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_rut">RUT *</Label>
                    <Input id="edit_rut" name="rut" value={formData.rut} onChange={handleInputChange} className="bg-slate-50" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_name">Nombre Completo *</Label>
                    <Input id="edit_name" name="name" value={formData.name} onChange={handleInputChange} className="bg-slate-50" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_birth_date">Fecha de Nacimiento *</Label>
                    <Input id="edit_birth_date" name="birth_date" type="date" value={formData.birth_date} onChange={handleBirthDateChange} className="bg-slate-50" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_nationality">Nacionalidad</Label>
                    <Input id="edit_nationality" name="nationality" value={formData.nationality} onChange={handleInputChange} className="bg-slate-50"/>
                  </div>
                  <div className="space-y-2">
                    <Label>Estado Civil</Label>
                    <Select value={formData.marital_status} onValueChange={(v) => handleSelectChange('marital_status', v)}>
                      <SelectTrigger className="bg-slate-50"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>
                        {MARITAL_STATUS.map(status => ( <SelectItem key={status} value={status}>{status}</SelectItem> ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* RSH */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 font-['Manrope']">Registro Social de Hogares</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_rsh_folio">Folio RSH</Label>
                    <Input id="edit_rsh_folio" name="rsh_folio" value={formData.rsh_folio} onChange={handleInputChange} className="bg-slate-50" />
                  </div>
                  <div className="space-y-2">
                    <Label>Tramo RSH</Label>
                    <Select value={formData.rsh_tramo} onValueChange={(v) => handleSelectChange('rsh_tramo', v)}>
                      <SelectTrigger className="bg-slate-50"><SelectValue placeholder="Seleccionar tramo" /></SelectTrigger>
                      <SelectContent>
                        {RSH_TRAMOS.map(tramo => ( <SelectItem key={tramo} value={tramo}>{tramo}</SelectItem> ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Domicilio */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 font-['Manrope']">Datos de Domicilio</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="edit_address">Dirección *</Label>
                    <Input id="edit_address" name="address" value={formData.address} onChange={handleInputChange} className="bg-slate-50" />
                  </div>
                  <div className="space-y-2">
                    <Label>Comuna *</Label>
                    <Select value={formData.comuna} onValueChange={(v) => handleSelectChange('comuna', v)}>
                      <SelectTrigger className="bg-slate-50"><SelectValue placeholder="Seleccionar comuna" /></SelectTrigger>
                      <SelectContent>
                        {CHILEAN_COMMUNES.map(comuna => ( <SelectItem key={comuna} value={comuna}>{comuna}</SelectItem> ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_phone">Teléfono *</Label>
                    <Input id="edit_phone" name="phone" value={formData.phone} onChange={handleInputChange} className="bg-slate-50" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_email">Correo Electrónico</Label>
                    <Input id="edit_email" name="email" type="email" value={formData.email} onChange={handleInputChange} className="bg-slate-50" />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={submitting}>
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</> : 'Actualizar Beneficiario'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre o RUT..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-50"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-slate-50" data-testid="status-filter">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="active">Activos</SelectItem>
                <SelectItem value="inactive">Inactivos</SelectItem>
                <SelectItem value="pending">Pendientes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : beneficiaries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <Users className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No hay beneficiarios</p>
              <p className="text-sm">Agregue el primer beneficiario para comenzar</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => requestSort('rut')}>
                      <div className="flex items-center">RUT <SortIcon columnKey="rut" /></div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => requestSort('name')}>
                      <div className="flex items-center">Nombre <SortIcon columnKey="name" /></div>
                    </TableHead>
                    <TableHead className="hidden md:table-cell cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => requestSort('comuna')}>
                      <div className="flex items-center">Comuna <SortIcon columnKey="comuna" /></div>
                    </TableHead>
                    <TableHead className="hidden lg:table-cell cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => requestSort('phone')}>
                      <div className="flex items-center">Teléfono <SortIcon columnKey="phone" /></div>
                    </TableHead>
                    <TableHead className="hidden lg:table-cell cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => requestSort('rsh_tramo')}>
                      <div className="flex items-center">Tramo RSH <SortIcon columnKey="rsh_tramo" /></div>
                    </TableHead>
                    <TableHead className="cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => requestSort('status')}>
                      <div className="flex items-center">Estado <SortIcon columnKey="status" /></div>
                    </TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedBeneficiaries.map((beneficiary) => (
                    <TableRow key={beneficiary.beneficiary_id} className="table-row-hover">
                      <TableCell className="font-medium">
                        {formatRut(beneficiary.rut)}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{beneficiary.name}</p>
                          <p className="text-xs text-slate-500 md:hidden flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {beneficiary.comuna}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{beneficiary.comuna}</TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="flex items-center gap-1 text-slate-600">
                          <Phone className="w-3 h-3" />
                          {beneficiary.phone}
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {beneficiary.rsh_tramo 
                          ? (Number(beneficiary.rsh_tramo) <= 1 && Number(beneficiary.rsh_tramo) > 0 
                              ? `${Math.round(Number(beneficiary.rsh_tramo) * 100)} %` 
                              : beneficiary.rsh_tramo)
                          : '-'}
                      </TableCell>
                      <TableCell>{getStatusBadge(beneficiary.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            asChild
                            title="Ver detalles"
                            className="bg-teal-50 text-teal-600 hover:bg-teal-100 hover:text-teal-700 w-8 h-8 rounded-full shadow-sm"
                            data-testid={`view-beneficiary-${beneficiary.beneficiary_id}`}
                          >
                            <Link to={`/beneficiaries/${beneficiary.beneficiary_id}`}>
                              <Eye className="w-4 h-4" />
                            </Link>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditClick(beneficiary)}
                            title="Editar beneficiario"
                            className="bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 w-8 h-8 rounded-full shadow-sm ml-1"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleStatus(beneficiary.beneficiary_id, beneficiary.status)}
                            title={beneficiary.status === 'active' ? "Desactivar" : "Activar"}
                            className={`w-8 h-8 rounded-full shadow-sm ml-1 ${
                              beneficiary.status === 'active' 
                                ? "bg-rose-50 text-rose-600 hover:bg-rose-100 hover:text-rose-700" 
                                : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700"
                            }`}
                          >
                            {beneficiary.status === 'active' ? (
                              <PowerOff className="w-4 h-4" />
                            ) : (
                              <Power className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {/* Pagination Controls */}
            {sortedBeneficiaries.length > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <span className="hidden sm:inline">Filas:</span>
                  <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                    <SelectTrigger className="w-[65px] h-8 bg-white border-slate-200">
                      <SelectValue placeholder={itemsPerPage} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-500">
                    {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedBeneficiaries.length)} de {sortedBeneficiaries.length}
                  </span>
                  
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 bg-white border-slate-200"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 bg-white border-slate-200"
                      disabled={currentPage === totalPages || totalPages === 0}
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Beneficiaries;
